import React from "react";

import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";

import { FieldV4, SelectV4 } from "../common/ui";
import { pageItems } from "../common/ui/PaginationV4";
import { optionText } from "../common/displayTextV4";

import { BTN_ACTS, type PickedState, type SaleFilter } from "../common/sequence/PackageV4";
import {
  ageRangeText,
  genderOf,
  lifespanText,
  PACKAGE_PAGE_SIZE,
  priceText,
  unpaidOrderOf,
  type MasterOption,
  type PackageItem,
  type PackageOrder,
} from "./packageModelV4";

// Types
type PackageSaleTabV4Props = Pick<PickedState, "buttonLoadCheck"> & {
  // data
  items: PackageItem[];
  orders: PackageOrder[];
  draft: SaleFilter;
  page: number;
  pageCount: number;
  total: number;
  loading: boolean;
  serviceTypes: MasterOption[];
  packageTypes: MasterOption[];
  // callback
  onChangeDraft: (draft: SaleFilter) => void;
  onSearch: (filter: SaleFilter) => void;
  onClear: () => void;
  onChangePage: (page: number) => void;
  onOpenDetail: (item: PackageItem) => void;
  onAdd: (item: PackageItem) => void;
};

const PackageSaleTabV4 = (props: PackageSaleTabV4Props) => {
  const { draft, page, total } = props;

  // Handler
  const handleChangeKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChangeDraft({ ...draft, keyword: e.target.value });
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      props.onSearch(draft);
    }
  };

  const handleChangeServiceType = (value: string) => {
    props.onChangeDraft({ ...draft, serviceType: value });
  };

  const handleChangePackageType = (value: string) => {
    props.onChangeDraft({ ...draft, packageType: value });
  };

  const handleSearch = () => {
    props.onSearch(draft);
  };

  const handlePrevPage = () => {
    props.onChangePage(page - 1);
  };

  const handleNextPage = () => {
    props.onChangePage(page + 1);
  };

  // Render
  const renderFilter = () => (
    <div className="hv4-pkg-filters">
      <FieldV4 label="ค้นหาชื่อ / รหัส" htmlFor="hv4-pkg-q" className="hv4-pkg-f-q">
        <input
          id="hv4-pkg-q"
          className="hv4-input"
          placeholder="ค้นหาชื่อบริการ"
          value={draft.keyword}
          onChange={handleChangeKeyword}
          onKeyDown={handleKeywordKeyDown}
        />
      </FieldV4>
      <FieldV4 label="ประเภทบริการ" htmlFor="hv4-pkg-s" className="hv4-pkg-f-s">
        <SelectV4
          id="hv4-pkg-s"
          placeholder="ทั้งหมด"
          options={[{ value: "", text: "ทั้งหมด" }, ...props.serviceTypes]}
          value={draft.serviceType}
          onChange={handleChangeServiceType}
        />
      </FieldV4>
      <FieldV4 label="ชนิด" htmlFor="hv4-pkg-k" className="hv4-pkg-f-k">
        <SelectV4
          id="hv4-pkg-k"
          placeholder="ทั้งหมด"
          options={[{ value: "", text: "ทั้งหมด" }, ...props.packageTypes]}
          value={draft.packageType}
          onChange={handleChangePackageType}
        />
      </FieldV4>
      <button
        type="button"
        className="hv4-pkg-bt hv4-pkg-bt-search"
        disabled={props.loading}
        onClick={handleSearch}
      >
        ค้นหา
      </button>
      <button type="button" className="hv4-pkg-bt hv4-pkg-bt-clear" onClick={props.onClear}>
        เคลียร์
      </button>
    </div>
  );

  const renderRow = (item: PackageItem) => {
    const gender = genderOf(item.gender);
    const inCart = !!unpaidOrderOf(props.orders, item.id);
    const adding = props.buttonLoadCheck?.[`${BTN_ACTS.ADD}_${item.id}`] === BLC_STATUS.LOADING;

    return (
      <div key={item.id} className="hv4-pkg-row" role="row">
        <span role="cell">{item.code}</span>
        <span role="cell">{optionText(props.packageTypes, item.package_type)}</span>
        <span role="cell">{item.name_en || item.name}</span>
        <span role="cell">{item.name}</span>
        <span role="cell" className="hv4-pkg-c hv4-pkg-gender" title={gender.text}>
          {gender.icon}
        </span>
        <span role="cell">{ageRangeText(item)}</span>
        <span role="cell" className="hv4-pkg-r">
          {priceText(item.price)}
        </span>
        <span role="cell" className="hv4-pkg-c">
          {lifespanText(item)}
        </span>
        <span role="cell" className="hv4-pkg-c">
          <button
            type="button"
            className="hv4-pkg-icon hv4-pkg-eye"
            aria-label={`ดูรายละเอียด ${item.name}`}
            onClick={() => props.onOpenDetail(item)}
          >
            👁
          </button>
        </span>
        <span role="cell" className="hv4-pkg-c">
          <button
            type="button"
            className="hv4-pkg-icon hv4-pkg-buy"
            aria-label={`เลือก ${item.name}`}
            disabled={adding}
            onClick={() => props.onAdd(item)}
          >
            {inCart ? "✓" : "🛒"}
          </button>
        </span>
      </div>
    );
  };

  const renderTable = () => (
    <div className="hv4-pkg-scroll">
      <div className="hv4-pkg-table hv4-pkg-sale" role="table" aria-label="แพ็กเกจที่เปิดขาย">
        <div className="hv4-pkg-row hv4-pkg-head" role="row">
          <span role="columnheader">รหัส</span>
          <span role="columnheader">ชนิด</span>
          <span role="columnheader">ชื่อแพ็กเกจ (EN)</span>
          <span role="columnheader">ชื่อแพ็กเกจ (TH)</span>
          <span role="columnheader" className="hv4-pkg-c hv4-pkg-gender-h">
            เพศ
          </span>
          <span role="columnheader">ช่วงอายุ</span>
          <span role="columnheader" className="hv4-pkg-r">
            ราคา
          </span>
          <span role="columnheader" className="hv4-pkg-c">
            ระยะเวลา
          </span>
          <span role="columnheader" className="hv4-pkg-c hv4-pkg-icon-h">
            รายละเอียด
          </span>
          <span role="columnheader" className="hv4-pkg-c hv4-pkg-icon-h">
            เลือก
          </span>
        </div>
        {props.items.length === 0 ? (
          <div className="hv4-pkg-empty" role="row">
            {props.loading ? "กำลังโหลด" : "ไม่พบแพ็กเกจที่เปิดขายตามเงื่อนไข"}
          </div>
        ) : (
          props.items.map(renderRow)
        )}
      </div>
    </div>
  );

  const renderPager = () => (
    <div className="hv4-pkg-pager">
      <span className="hv4-pkg-pager-text">
        {total
          ? `แสดง ${(page - 1) * PACKAGE_PAGE_SIZE + 1}–${Math.min(
              page * PACKAGE_PAGE_SIZE,
              total,
            )} จาก ${total} รายการ`
          : props.loading
            ? ""
            : "ไม่พบรายการ"}
      </span>
      <span className="hv4-pkg-pager-pages">
        <button
          type="button"
          className="hv4-pkg-pg"
          aria-label="หน้าก่อนหน้า"
          onClick={handlePrevPage}
        >
          ‹
        </button>
        {pageItems(page, props.pageCount).map((n, index) =>
          n === null ? (
            <span key={`gap-${index}`} className="hv4-pkg-pg-gap">
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              className={`hv4-pkg-pg${n === page ? " hv4-pkg-pg--on" : ""}`}
              aria-current={n === page ? "page" : undefined}
              onClick={() => props.onChangePage(n)}
            >
              {n}
            </button>
          ),
        )}
        <button
          type="button"
          className="hv4-pkg-pg"
          aria-label="หน้าถัดไป"
          onClick={handleNextPage}
        >
          ›
        </button>
      </span>
    </div>
  );

  return (
    <section className="hv4-card hv4-pkg-panel">
      {renderFilter()}
      {renderTable()}
      {renderPager()}
    </section>
  );
};

PackageSaleTabV4.displayName = "PackageSaleTabV4";

export default PackageSaleTabV4;
